/**
 * 姿势应用（P0-3b，D-22 验收的执行件）
 *
 * 把三层合成后的 BoneMap（骨骼名 → 欧拉弧度）套到真实骨架上。
 * 这里刻意只做"名字对上就设 rotation"这一件事，不做任何轴序/符号魔法——
 * 那是 D-01 的标定任务，必须等真实模型逐滑杆核对，提前"优化"就是猜。
 *
 * ⚠️ 两条用实测换来的规矩：
 *  1. **重置 = 还原 rest 快照，不是归零**。Mixamo 骨架导出成 glTF 后，部分骨骼的
 *     rest rotation 本身非零（bind 姿态存在节点 TRS 里），归零会毁掉 A-pose，
 *     表现为"重置后角色手臂插进身体里"。rest 必须在加载完成时捕获一次。
 *  2. **套姿势前必须保证骨架名已规范化**（mixamorigHips 形式，无冒号）。
 *     本项目在 GLB 字节层已统一（D-18 关闭），这里再检测到带冒号的名字就报警，
 *     防止有人绕过管线手塞了一个未规范的模型导致 977 姿势全静默失效。
 */
import { Euler, Quaternion } from 'three';
import type { Object3D, SkinnedMesh } from 'three';

import type { BoneMap } from '../../model/pose';
import { BONE_NAMES } from '../../model/rigConstants';
import { STD_FBX_REST } from '../../model/stdFbxRest';

/** 骨骼对象 → rest 欧拉角快照。
 * ⚠️ 2026-09-28 第三轮实测定案：**必须按对象引用存，不能按名字存**——
 * 重名骨（多套骨架副本）会让名字键的后存覆盖先存，还原时把副本的 rest
 * 安到真骨上，表现为"还原 rest 反而扭曲"（实测：肌肉男 648 键/便装 111 键）。
 */
export type RestSnapshot = Map<Object3D, { x: number; y: number; z: number; order: string }>;

export interface ApplyReport {
  /** 成功设旋的骨骼数 */
  applied: number;
  /** 姿势数据里有、骨架上找不到的骨骼名（管线失配的信号，必须上报不能吞） */
  missingOnSkeleton: string[];
  /** 骨架里有、姿势数据没给的骨骼名——正常情况（末端骨可不动），仅诊断用 */
  untouched: string[];
}

const KNOWN = new Set(BONE_NAMES as readonly string[]);

/**
 * 运行时把骨名就地规范化为姿势库形式 `mixamorigHips`。两种实测形态都要处理：
 *  - `mixamorig:Hips` / `mixamorig2:Hips`（带命名空间冒号）
 *  - `mixamorig2Hips`（⚠️ 2026-09-28 node 实测：FBXLoader 自己已去冒号但**保留序号**，
 *    只按冒号匹配的正则对这种形态完全失效——便装角色"缺失 65"的真因）
 * 重命名 Object3D.name 不影响蒙皮（joints 按对象引用而非名字），安全。
 */
export function normalizeBoneNames(root: Object3D): number {
  let renamed = 0;
  root.traverse((n) => {
    const m = /^mixamorig(\d*):?(.+)$/.exec(n.name);
    if (m !== null) {
      const target = 'mixamorig' + m[2];
      if (target !== n.name) {
        n.name = target;
        renamed += 1;
      }
    }
  });
  return renamed;
}

function isBone(node: Object3D): boolean {
  return node.name.startsWith('mixamorig');
}

/**
 * 遍历骨架取所有 mixamorig* 节点。
 * 名字里还带冒号说明模型绕过了字节层规范——直接抛错，让问题死在开发期，
 * 而不是线上"角色一动不动"。
 */
export function collectBones(root: Object3D): Object3D[] {
  const bones: Object3D[] = [];
  root.traverse((n) => {
    if (n.name.includes(':') && n.name.startsWith('mixamorig')) {
      throw new Error(
        `骨骼名未规范化（含命名空间冒号）：${n.name}——` +
        '请先跑 scripts/normalize_glb_bones.py，否则 977 姿势将静默失效',
      );
    }
    if (isBone(n)) {
      bones.push(n);
    }
  });
  return bones;
}

/** 加载完成后立刻调用一次：按蒙皮权威集捕获 rest 快照（键为对象引用） */
export function captureRest(root: Object3D): RestSnapshot {
  const snap: RestSnapshot = new Map();
  for (const bone of boundBones(root)) {
    snap.set(bone, {
      x: bone.rotation.x,
      y: bone.rotation.y,
      z: bone.rotation.z,
      order: bone.rotation.order,
    });
  }
  return snap;
}

export function restoreRest(snap: RestSnapshot): number {
  let restored = 0;
  for (const [bone, r] of snap) {
    bone.rotation.set(r.x, r.y, r.z, r.order as 'XYZ');
    restored += 1;
  }
  return restored;
}

/** 套姿势前取"真正参与蒙皮的骨骼"集合。
 *  ⚠️ 2026-09-28 node 实测：扛斧肌肉男 FBX 里有 648 个 mixamorig 节点、大量重名副本
 *  （披风多骨架残留），按 traverse 建名字表会命中未绑定的副本——转了不动的根因。
 *  mesh.skeleton.bones 才是蒙皮权威集；无 SkinnedMesh 时退回 traverse（单测/占位场景）。
 */
function boundBones(root: Object3D): Object3D[] {
  const seen = new Set<Object3D>();
  root.traverse((o) => {
    const skinned = o as SkinnedMesh;
    if (skinned.isSkinnedMesh && skinned.skeleton) {
      for (const b of skinned.skeleton.bones) {
        seen.add(b);
      }
    }
  });
  if (seen.size > 0) {
    return [...seen];
  }
  return collectBones(root).filter((b) => KNOWN.has(b.name));
}

/** 套用三层合成结果。
 * ⚠️ 坐标系修正（2026-09-28 第四轮标定，node 数值验证 err≤0.02°）：
 *   姿势库是 Mixamo 标准 FBX 局部系的绝对欧拉角；Blender 导出对个别骨（如 Hips）
 *   烘焙了 90° 轴修正 → 直接赋值会让全身经根骨偏 90°（"躺平"真因）。
 *   传入 captureRest 快照即启用逐骨修正：local = (rest_b ∘ std⁻¹) ∘ pose；
 *   不传则保持旧行为（直接赋值，仅供单测/确实无需修正的模型）。
 * ⚠️ 多副本同名骨：应用到全部蒙皮实例（身体/衣服分绑不同副本时都要动）。
 */
export function applyBoneMap(
  root: Object3D,
  bones: BoneMap,
  rest?: RestSnapshot,
): ApplyReport {
  const skeleton = boundBones(root);
  const byName = new Map<string, Object3D[]>();
  for (const b of skeleton) {
    const list = byName.get(b.name);
    if (list === undefined) {
      byName.set(b.name, [b]);
    } else {
      list.push(b);
    }
  }
  const missingOnSkeleton: string[] = [];
  let applied = 0;
  const e = new Euler();
  const qPose = new Quaternion();
  for (const [name, v] of Object.entries(bones)) {
    const targets = byName.get(name);
    if (targets === undefined) {
      missingOnSkeleton.push(name);
      continue;
    }
    for (const bone of targets) {
      e.set(v[0], v[1], v[2], 'XYZ');
      qPose.setFromEuler(e);
      const r0 = rest?.get(bone);
      const std = STD_FBX_REST[name];
      if (r0 !== undefined && std !== undefined) {
        const qRest = new Quaternion().setFromEuler(
          new Euler(r0.x, r0.y, r0.z, r0.order as 'XYZ'),
        );
        const qStd = new Quaternion(std[0], std[1], std[2], std[3]);
        bone.quaternion
          .copy(qRest)
          .multiply(qStd.invert())
          .multiply(qPose);
      } else {
        bone.quaternion.copy(qPose);
      }
      applied += 1;
    }
  }
  const seenNames = new Set<string>();
  const untouched: string[] = [];
  for (const b of skeleton) {
    if (KNOWN.has(b.name) && bones[b.name] === undefined && !seenNames.has(b.name)) {
      seenNames.add(b.name);
      untouched.push(b.name);
    }
  }
  return { applied, missingOnSkeleton, untouched };
}

/**
 * 道具网格运行时剔除（D-24 用户裁定 2026-09-28：素体只展示骨骼绑定/编辑，不要道具）。
 * FBX 直加载路线下 Blender 侧的剔除不再作用于运行区，故在加载后按名字模式移除。
 * 只删匹配到的 SkinnedMesh 节点，不碰骨骼（蒙皮权重不受影响）。
 */
const PROP_MESH_RE = /weapon|axe|sword|shield|gun|knife|rifle|prop/i;
export function stripPropMeshes(root: Object3D): string[] {
  const removed: string[] = [];
  const visit = (node: Object3D): void => {
    for (const child of [...node.children]) {
      const skinned = child as SkinnedMesh;
      if (skinned.isSkinnedMesh && PROP_MESH_RE.test(child.name)) {
        node.remove(child);
        removed.push(child.name);
        continue; // 已移除的子树不再下探
      }
      visit(child);
    }
  };
  visit(root);
  return removed;
}

/**
 * 自动拟合缩放（2026-09-28 node 实测：Mixamo FBX 单位是 cm，bbox 高 174，
 * FBXLoader 不做 cm→m 换算，浏览器里角色"巨大"）。
 * 规则：实测高度 > 可疑阈值（10 米，真人不可能是这个数）才介入，
 * 归一到 targetHeight；正常尺寸模型返回 1，绝不"顺手缩放"。
 */
export const SUSPICIOUS_HEIGHT_M = 10;
export const TARGET_HEIGHT_M = 1.75;
export function computeAutoFitScale(bboxHeight: number): number {
  if (!Number.isFinite(bboxHeight) || bboxHeight <= 0) {
    return 1;
  }
  if (bboxHeight <= SUSPICIOUS_HEIGHT_M) {
    return 1;
  }
  return TARGET_HEIGHT_M / bboxHeight;
}
